import {
  Button,
  FlexBox,
  Icon,
  IconButton,
  Spinner,
  Tooltip,
} from '@dixa/lib-shared-habla';
import { colors } from '@dixa/lib-shared-tokens';
import classNames from 'classnames';
import type {
  BasicFilterControl,
  CustomAttributeFilterControl,
  DashboardFilter,
  FilterControl,
  InstrumentPanel,
} from 'lib-analytics-types';
import { ampli } from 'lib-shared-tracking';
import isEqual from 'lodash/isEqual';
import { memo, useCallback, useEffect, useMemo } from 'react';
import { useList, useUpdateEffect } from 'react-use';

import { useAnalyticsState } from 'analytics/components/panels/utils/useAnalyticsState/useAnalyticsState';
import { analyticsApi } from 'apis/analytics';

import { isOptionalOperator } from '../../utils/filters/isOptionalOperator';
import { AnalyticsFilter, AnalyticsFilterChangeCallback } from '../filter';

import styles from './analyticsFilters.module.scss';
import { AnalyticsCustomAttributesFilter } from './components/customAttributes/analyticsCustomAttributesFilter';

type AnalyticsFiltersProps = {
  /** The parent instrument panel, if relevant. */
  panel?: InstrumentPanel;
  /** The support dashboard filters. */
  dashboardFilters: readonly DashboardFilter[];
  /** Whether the filters are collapsed. */
  isCollapsed?: boolean;
  /** Callback for when the filters change. */
  onChange: (filters: FilterControl[]) => void;
};

export const AnalyticsFilters = memo(function AnalyticsFilters({
  panel,
  dashboardFilters,
  isCollapsed = false,
  onChange,
}: AnalyticsFiltersProps): JSX.Element {
  const { dashboard, context, filters } = useAnalyticsState(panel);

  const [draftFilters, draftFilterSetters] = useList<FilterControl>([
    ...filters,
  ]);

  const isDirty = useMemo(
    () => !isEqual(filters, draftFilters),
    [filters, draftFilters],
  );

  const [readFiltersQuery, { data, isFetching, isError }] =
    analyticsApi.useLazyReadFiltersQuery();

  const readFilters = (): void => {
    const preferCacheValue = true;
    readFiltersQuery({ context, id: dashboard.id }, preferCacheValue);
  };

  // Keeps the draft filters in sync with the filters in the store, in case the store filters
  // are changed externally, for example when filters are normalized between changing dashboards.
  useUpdateEffect(() => {
    if (isDirty) {
      draftFilterSetters.set([...filters]);
    }
  }, [filters]);

  // Fetches the filters data on component mount or when the dashboard context changes.
  useEffect(() => {
    readFilters();
  }, [context]);

  /** Adds, updates or removes a filter from the draft state. */
  const handleChange = useCallback<
    AnalyticsFilterChangeCallback<FilterControl>
  >(
    ({ op, filter }) => {
      const predicate = (draft: FilterControl) =>
        draft.field === filter.field &&
        (filter.field === 'custom_fields' && draft.field === 'custom_fields'
          ? filter.attribute_id === draft.attribute_id
          : true);

      if (op === 'upsert') {
        draftFilterSetters.upsert(predicate, filter);
      } else if (op === 'remove') {
        const index = draftFilters.findIndex(predicate);
        draftFilterSetters.removeAt(index);
      }
    },
    [draftFilterSetters, draftFilters],
  );

  /**
   * Updates the analytics state with the selected filters upon clicking
   * "Apply".
   */
  const handleApply = () => {
    onChange(draftFilters);

    ampli.analyticsFiltersApplied({
      dashboard: dashboard.title,
      panel: panel?.title,
      filters: draftFilters.map(({ field }) => field),
    });
  };

  /** Clears all filters. */
  const handleClearAll = () => {
    draftFilterSetters.clear();

    // We only clear the filters in the store if there previously were any filters applied, to avoid
    // an unnecessary state update that would cause the instrument data to be reloaded.
    if (filters.length) {
      onChange([]);

      ampli.analyticsFiltersCleared({
        dashboard: dashboard.title,
        panel: panel?.title,
      });
    }
  };

  /** Returns the enriched dashboard filter with values and operators. */
  const getDashboardFilter = useCallback(
    (filter: DashboardFilter): DashboardFilter =>
      data?.data.filters.find(({ field }) => field === filter.field) ?? filter,
    [data?.data.filters],
  );

  return (
    <div
      className={classNames(styles.filters, {
        [styles.filtersCollapsed]: isCollapsed,
      })}
    >
      {isFetching && <Spinner size={20} />}

      {isError && (
        <Tooltip content="An error occurred. Click to retry.">
          <FlexBox>
            <Icon
              className={styles.pointer}
              name="exclamation"
              color={colors.red['500']}
              size={20}
              onClick={readFilters}
            />
          </FlexBox>
        </Tooltip>
      )}

      <div className={styles.dropdowns}>
        {dashboardFilters.map((filter) => {
          if (filter.field === 'custom_fields') {
            const draftControls = draftFilters.filter(
              ({ field }) => field === 'custom_fields',
            ) as CustomAttributeFilterControl[];

            return (
              <AnalyticsCustomAttributesFilter
                key={filter.field}
                panel={panel}
                field="custom_fields"
                draftControls={draftControls}
                buttonProps={{
                  disabled: isFetching,
                  label: draftControls.length || undefined,
                }}
                onChange={handleChange}
              />
            );
          }

          const control = filters.find(
            ({ field }) => field === filter.field,
          ) as BasicFilterControl;

          const draftControl = draftFilters.find(
            ({ field }) => field === filter.field,
          ) as BasicFilterControl;

          const dashboardFilter = getDashboardFilter(filter);

          return (
            <AnalyticsFilter<BasicFilterControl>
              key={filter.field}
              panel={panel}
              field={filter.field}
              availableValues={dashboardFilter.values}
              selectedValues={draftControl?.values}
              pinnedValues={control?.values}
              availableOperators={dashboardFilter.operators}
              selectedOperator={draftControl?.operator}
              isMultiple
              isSelectionDisabled={isOptionalOperator(draftControl?.operator)}
              hasDeterminateSelection
              onChange={handleChange}
              buttonProps={{
                variant: 'tertiary',
                label: draftControl
                  ? draftControl.values.length || 1
                  : undefined,
                disabled: isFetching,
              }}
            >
              {dashboardFilter.label ?? filter.field}
            </AnalyticsFilter>
          );
        })}
      </div>

      <Button
        className={classNames(styles.apply, {
          [styles.applyPulse]: isDirty,
        })}
        variant="primary"
        disabled={isFetching || !isDirty}
        onClick={handleApply}
      >
        Apply
      </Button>

      <IconButton
        variant="tertiary"
        icon="trash"
        disabled={!draftFilters.length}
        onClick={handleClearAll}
      />
    </div>
  );
});
